import { supabase } from "~/integrations/supabase/client";
import type {
  RawMaterial,
  RawMaterialFormData,
  RawMaterialPrice,
} from "~/types/purchasing";

export const rawMaterialService = {
  async getAll(): Promise<RawMaterial[]> {
    const { data, error } = await supabase
      .from("raw_materials")
      .select("*")
      .order("name", { ascending: true });

    if (error) throw error;
    return data as RawMaterial[];
  },

  async getById(id: string): Promise<RawMaterial> {
    const { data, error } = await supabase
      .from("raw_materials")
      .select("*")
      .eq("id", id)
      .single();

    if (error) throw error;
    return data as RawMaterial;
  },

  async create(data: RawMaterialFormData): Promise<RawMaterial> {
    const { data: material, error } = await supabase
      .from("raw_materials")
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return material as RawMaterial;
  },

  async update(
    id: string,
    data: Partial<RawMaterialFormData>
  ): Promise<RawMaterial> {
    const { data: material, error } = await supabase
      .from("raw_materials")
      .update(data)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return material as RawMaterial;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from("raw_materials")
      .delete()
      .eq("id", id);

    if (error) throw error;
  },

  async getLowStock(): Promise<RawMaterial[]> {
    const allMaterials = await this.getAll();
    return allMaterials.filter(
      (material) =>
        material.minimum_stock_level !== null &&
        material.current_stock < material.minimum_stock_level
    );
  },

  // Price history methods
  async getPriceHistory(rawMaterialId: string): Promise<RawMaterialPrice[]> {
    const { data, error } = await supabase
      .from("raw_material_prices")
      .select("*, supplier:suppliers(*)")
      .eq("raw_material_id", rawMaterialId)
      .order("effective_date", { ascending: false });

    if (error) throw error;
    return data as RawMaterialPrice[];
  },

  async getLatestPrice(
    rawMaterialId: string,
    supplierId?: string
  ): Promise<number | null> {
    const { data, error } = await supabase.rpc("get_latest_material_price", {
      p_raw_material_id: rawMaterialId,
      p_supplier_id: supplierId ?? null,
    });

    if (error) throw error;
    return data;
  },

  async addPrice(data: {
    raw_material_id: string;
    supplier_id?: string;
    unit_price: number;
    currency: "CLP" | "USD";
    effective_date: string;
  }): Promise<RawMaterialPrice> {
    const { data: price, error } = await supabase
      .from("raw_material_prices")
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return price as RawMaterialPrice;
  },
};
